import { create } from "zustand";
import { api } from "../api";

// Per-user, not per-field and not per-session: "the same when they come back or open a new field"
// was the requirement, so this rides the same lifecycle as the soil-units lens — server-owned, with
// localStorage as the mirror. Same send-gate as `mass_basis`: the PATCH omits
// `terrain_exaggeration` until `/users/me` has shown the key.

export const MIN_EXAGGERATION = 0;
export const MAX_EXAGGERATION = 50;
export const DEFAULT_EXAGGERATION = 30;

const LS_KEY = "sm.terrainExaggeration";

const clamp = (v: number): number =>
  Number.isFinite(v)
    ? Math.min(MAX_EXAGGERATION, Math.max(MIN_EXAGGERATION, v))
    : DEFAULT_EXAGGERATION;

let serverHasKey = false;

interface TerrainPrefState {
  exaggeration: number;
  /* * true once the user has moved it — so a future default change can still reach untouched accounts */
  touched: boolean;
  set: (v: number) => void;
  hydrateFromServer: (o: Record<string, unknown> | null | undefined) => void;
}

function load(): { exaggeration: number; touched: boolean } {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw == null)
      return { exaggeration: DEFAULT_EXAGGERATION, touched: false };
    const n = Number(raw);
    // 0 is a REAL value here (terrain off), so it must survive the falsy check that would drop it
    return Number.isFinite(n)
      ? { exaggeration: clamp(n), touched: true }
      : { exaggeration: DEFAULT_EXAGGERATION, touched: false };
  } catch {
    return { exaggeration: DEFAULT_EXAGGERATION, touched: false };
  }
}

export const useTerrainPref = create<TerrainPrefState>((set, get) => ({
  ...load(),

  set: (v) => {
    const exaggeration = clamp(v);
    set({ exaggeration, touched: true });
    try {
      localStorage.setItem(LS_KEY, String(exaggeration));
    } catch {
      // a private-mode failure must not break the map; the value still applies for this session
    }
    if (serverHasKey) {
      void api
        .updateSoilUnits({ terrain_exaggeration: exaggeration } as never)
        .catch(() => {
          // a failed save leaves the local value in place rather than reverting under the user's cursor
        });
    }
  },

  hydrateFromServer: (o) => {
    if (!o) return;
    if ("terrain_exaggeration" in o) {
      serverHasKey = true;
      const v = o.terrain_exaggeration;
      // NULL = never set: keep whatever the user has locally rather than stamping the default over it
      if (typeof v === "number" && Number.isFinite(v))
        set({ exaggeration: clamp(v), touched: true });
    }
    void get();
  },
}));

/* * Read the current value outside React (the map's imperative terrain wiring). */
export const terrainExaggeration = (): number =>
  useTerrainPref.getState().exaggeration;
